/* eslint-disable no-unused-vars */
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;

/**
 * Handler for equipment tags
 */
export class EquipmentHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process equipment
     */
    shouldProcess(type, displaySettings) {
        return (displaySettings.showTags && (type === 'equipment' || type === 'equipment_2d')) || 
               (displaySettings.showAutoEquipment && this.isViewer2d() && type === 'auto_equipment');
    }
    
    /**
     * Collect equipment data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected equipment data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Get equipment type from extension
        let type = '';
        if (this.extension.currentTagType) {
            type = this.extension.currentTagType;
        } else {
            // Try to determine type based on available information
            if (displaySettings.showTags) {
                type = this.isViewer2d() ? 'equipment_2d' : 'equipment';
            } else if (displaySettings.showAutoEquipment && this.isViewer2d()) {
                type = 'auto_equipment';
            } else {
                return data; // No applicable type
            }
        }
        
        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        let equipments = this.getEquipmentsByType(type);

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                equipments = equipments.filter(equipment => isolatedTags.has(equipment.id));
            }
        }
        
        // Only apply clustering after filtering in 2D view
        if (this.isViewer2d() && equipments.length > 0) {
            // Apply clustering logic
            const { items, clusters } = this.getTagsClustersAndItems2D(equipments, viewerData.position);
                        
            // Process clusters
            this.processClusters(clusters, data, 'equipment-cluster');
            
            // Update to use filtered items
            equipments = items;
        }
        
        equipments.forEach(equipment => {
            // Get point in viewer space
            let point = this.getPointForEquipment(equipment, type);
            if (!point) return;
            
            // Create point cloud data
            const pcData = this.createEquipmentData(equipment, point);
            
            // Add to category collection
            const category = equipment.category ?? 1;
            data[category] ? data[category].push(pcData) : data[category] = [pcData];
        });
        
        return data;
    }
    
    /**
     * Get equipments for specific type
     * @param {string} type - Equipment type
     * @returns {Array} Equipment items
     */
    getEquipmentsByType(type) {
        if (type === 'equipment') {
            // Regular equipment
            const allEquipments = store.state.forgeService.getEquipmentsInLevel(store.state.selectedLevel);
            return this.isViewer2d() ? allEquipments.filter(e => !e.level2D) : allEquipments;
        } else if (type === 'equipment_2d') {
            // 2D equipment
            return store.state.forgeService.getEquipmentsInLevel(store.state.selectedLevel).filter(e => !!e.level2D);
        } else if (type === 'auto_equipment') {
            // Auto equipment
            return store.state.forgeService.getAutoEquipmentsInLevel(store.state.selectedLevel);
        }
        return [];
    }
    
    /**
     * Check if equipment should be skipped based on filters
     * @param {Object} equipment - Equipment object
     * @param {string} type - Tag type
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @returns {boolean} Should skip equipment
     */
    shouldSkipEquipment(equipment, type, isGlobalSearchFilter) {
        if (type === 'equipment' || type === 'equipment_2d') {
            return isGlobalSearchFilter && !store.state.filteredTags.includes(equipment);
        } else if (type === 'auto_equipment') {
            const filteredAutoTags = store.getters['GET_AUTOMATIC_TAGS'];
            return isGlobalSearchFilter && !filteredAutoTags.some(t => t.id == equipment.id);
        }
        return false;
    }
    
    /**
     * Get point in viewer space for equipment
     * @param {Object} equipment - Equipment object
     * @param {string} type - Tag type
     * @returns {Object|null} Point in viewer space
     */
    getPointForEquipment(equipment, type) {
        if (!equipment) {
            debugWarn('equipmentHandler', 'Equipment is null or undefined');
            return null;
        }

        let point;
        
        if (type === 'auto_equipment') {
            equipment.point = Object.assign(new THREE.Vector3(), equipment.point);
            point = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(equipment.point);
            point.x += .001; // Offset for visibility
        } else {
            point = this.isViewer2d() 
                ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(equipment.point) 
                : equipment.getPointInView();
                
            // Skip if outside crop box in 3D view
            if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
                return null;
            }
            
            if (this.isViewer2d()) point.z = .1;
        }
        
        return point;
    }
    
    /**
     * Create equipment data for point cloud
     * @param {Object} equipment - Equipment object
     * @param {Object} point - Point in viewer space
     * @returns {Object} Point cloud data
     */
    createEquipmentData(equipment, point) {
        const _this = this;
        return {
            name: equipment.name,
            thumbnailURL: equipment.thumbnailUrl,
            object: equipment,
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_EQUIPMENT_TAG_BY_ID', equipment.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
    }
}
